import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@layout/Layout';
import * as AssessmentService from '@services/AssessmentService';
import { useAppTheme } from '@contexts/ThemeContext';
import { ArrowLeft, Save, AlertCircle, CheckCircle } from 'lucide-react';
import Spinner from './components/Spinner';
import './CreateAssessment.css';

const CreateAssessment: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const navigate = useNavigate();

  const [assessmentTypes, setAssessmentTypes] = useState<AssessmentService.AssessmentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState<AssessmentService.CreateAssessmentData>({
    assessmentTitle: '',
    course: '',
    creator: '',
    goal: '',
    suggestCareer: false,
    startTime: '',
    endTime: '',
    assessment_type_id: 0
  });

  useEffect(() => {
    fetchAssessmentTypes();
  }, []);

  const fetchAssessmentTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await AssessmentService.getAssessmentTypes();
      if (response.success && response.data) {
        setAssessmentTypes(response.data);
      } else {
        throw new Error(response.error?.message || 'Failed to fetch assessment types');
      }
    } catch (err: any) {
      console.error('Error fetching assessment types:', err);
      setError(err.message || 'Failed to load assessment types');
    } finally {
      setLoadingTypes(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'assessment_type_id') {
      setFormData((prev) => ({
        ...prev,
        [name]: parseInt(value, 10)
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validation
    if (!formData.assessmentTitle.trim()) {
      setError('Assessment title is required');
      setLoading(false);
      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
      return;
    }

    if (!formData.assessment_type_id) {
      setError('Please select an assessment type');
      setLoading(false);
      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
      return;
    }

    // Track start time to ensure minimum 1 second loading
    const startTime = Date.now();

    try {
      const response = await AssessmentService.createAssessment(formData);

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time to ensure minimum 1 second loading
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      if (response.success && response.data) {
        setLoading(false);
        setSuccess('Assessment created successfully!');

        // Navigate to module-assessment-mapping page with the newly created assessment ID
        const assessmentId = response.data.assessmentId || response.data.data?.assessmentId;

        // Auto-hide success message and navigate after 1 second
        setTimeout(() => {
          setSuccess(null);

          // Navigate to module-assessment mapping for all assessment types
          navigate(`/module-assessment-mapping?assessmentId=${assessmentId}`);
        }, 1000);
      } else {
        throw new Error(response.error?.message || 'Failed to create assessment');
      }
    } catch (err: any) {
      console.error('Error creating assessment:', err);

      // Calculate remaining time to meet minimum 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      // Wait for remaining time
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      setLoading(false);
      setError(err.message || 'Failed to create assessment');

      // Auto-hide error after 1 second
      setTimeout(() => setError(null), 1000);
    }
  };

  const handleCancel = () => {
    navigate('/assessment-dashboard');
  };

  return (
    <Layout>
      <div className="create-assessment" data-theme={isDarkMode ? 'dark' : 'light'}>
        <div className="create-assessment-header">
          <h1 className="page-title">Create New Assessment</h1>
        </div>

        {error && (
          <div className="alert alert-error">
            <AlertCircle size={20} />
            <span>{error}</span>
          </div>
        )}

        {success && (
          <div className="alert alert-success">
            <CheckCircle size={20} />
            <span>{success}</span>
          </div>
        )}

        <form className="assessment-form" onSubmit={handleSubmit}>
          <div className="form-section">
            <h2 className="section-title">Basic Information</h2>

            <div className="form-group">
              <label htmlFor="assessmentTitle" className="form-label required">
                Assessment Title
              </label>
              <input
                type="text"
                id="assessmentTitle"
                name="assessmentTitle"
                className="form-input"
                value={formData.assessmentTitle}
                onChange={handleInputChange}
                placeholder="Enter assessment title"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="assessment_type_id" className="form-label required">
                Assessment Type
              </label>
              {loadingTypes ? (
                <Spinner size="small" fullPage={false} />
              ) : (
                <select
                  id="assessment_type_id"
                  name="assessment_type_id"
                  className="form-select"
                  value={formData.assessment_type_id}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select assessment type</option>
                  {assessmentTypes.map((type) => (
                    <option key={type.assessment_type_id} value={type.assessment_type_id}>
                      {type.assessment_type}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="course" className="form-label">
                Course
              </label>
              <input
                type="text"
                id="course"
                name="course"
                className="form-input"
                value={formData.course}
                onChange={handleInputChange}
                placeholder="Enter course name (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="creator" className="form-label">
                Creator
              </label>
              <input
                type="text"
                id="creator"
                name="creator"
                className="form-input"
                value={formData.creator}
                onChange={handleInputChange}
                placeholder="Enter creator name (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="goal" className="form-label">
                Goal/Description
              </label>
              <textarea
                id="goal"
                name="goal"
                className="form-textarea"
                value={formData.goal}
                onChange={handleInputChange}
                placeholder="Enter assessment goal or description (optional)"
                rows={4}
              />
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Schedule</h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="startTime" className="form-label">
                  Start Time
                </label>
                <input
                  type="datetime-local"
                  id="startTime"
                  name="startTime"
                  className="form-input"
                  value={formData.startTime}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime" className="form-label">
                  End Time
                </label>
                <input
                  type="datetime-local"
                  id="endTime"
                  name="endTime"
                  className="form-input"
                  value={formData.endTime}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2 className="section-title">Options</h2>

            <div className="form-group-checkbox">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="suggestCareer"
                  checked={formData.suggestCareer}
                  onChange={handleInputChange}
                  className="form-checkbox"
                />
                <span>Suggest Career Paths</span>
              </label>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || loadingTypes}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  <span>Creating...</span>
                </>
              ) : (
                <>
                  <Save size={20} />
                  <span>Create Assessment</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default CreateAssessment;
